import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

const base =
  'inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded border border-foreground font-mono text-sm text-foreground transition-colors hover:bg-foreground hover:text-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:pointer-events-none disabled:opacity-50';

// All sizes guarantee a ≥44px hit target per WCAG 2.5.5 / Apple HIG. Visual
// height is set with `min-h` so flex centering takes care of internal padding.
// `md` is the canonical outline-mono CTA per DESIGN.md §5.
// `sm` is the sticky-header variant (tighter horizontal padding).
// `hero` matches the 46px App Store badge height for hero alignment.
const sizes = {
  sm: 'min-h-[44px] px-4',
  md: 'min-h-[44px] px-6',
  hero: 'min-h-[46px] h-[46px] px-6',
} as const;

export type MonoCTASize = keyof typeof sizes;

interface CommonProps {
  size?: MonoCTASize;
  className?: string;
  children: ReactNode;
}

type LinkProps = CommonProps & { to: string };
type AnchorProps = CommonProps & { href: string } & Omit<ComponentPropsWithoutRef<'a'>, 'href' | 'className' | 'children'>;
type ButtonProps = CommonProps & Omit<ComponentPropsWithoutRef<'button'>, 'className' | 'children'>;

export function MonoCTA(props: LinkProps | AnchorProps | ButtonProps) {
  const cls = cn(base, sizes[props.size ?? 'md'], props.className);

  if ('to' in props) {
    return (
      <Link to={props.to} className={cls}>
        {props.children}
      </Link>
    );
  }

  if ('href' in props) {
    const { size, className, children, ...rest } = props;
    return (
      <a className={cls} {...rest}>
        {children}
      </a>
    );
  }

  const { size, className, children, ...rest } = props;
  return (
    <button className={cls} {...rest}>
      {children}
    </button>
  );
}
